import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// PT-BR translations (base language)
import navigationPt from '../i18n/public/navigation.json';
import homePt from '../i18n/public/home.json';
import solutionsPt from '../i18n/public/solutions.json';
import aboutPt from '../i18n/public/about.json';
import footerPt from '../i18n/public/footer.json';
import faqPt from '../i18n/public/faq.json';

// EN translations
import navigationEn from '../i18n/public/en/navigation.json';
import homeEn from '../i18n/public/en/home.json';
import solutionsEn from '../i18n/public/en/solutions.json';
import aboutEn from '../i18n/public/en/about.json';
import footerEn from '../i18n/public/en/footer.json';
import cookieConsentEn from '../i18n/public/en/cookieConsent.json';
import faqEn from '../i18n/public/en/faq.json';

// ES translations
import navigationEs from '../i18n/public/es/navigation.json';
import homeEs from '../i18n/public/es/home.json';
import solutionsEs from '../i18n/public/es/solutions.json';
import aboutEs from '../i18n/public/es/about.json';
import footerEs from '../i18n/public/es/footer.json';
import cookieConsentEs from '../i18n/public/es/cookieConsent.json';
import faqEs from '../i18n/public/es/faq.json';

// Multi-language files (already have pt-br, en, es keys)
import careersData from '../i18n/public/careers.json';
import legalData from '../i18n/public/legal.json';
import cookieConsentPt from '../i18n/public/cookieConsent.json';

// Build translation objects for each language
const ptBR = {
    navigation: navigationPt,
    home: homePt,
    solutions: solutionsPt,
    about: aboutPt,
    footer: footerPt,
    faq: faqPt,
    careers: careersData['pt-br'],
    legal: legalData['pt-br'],
    cookieConsent: cookieConsentPt['pt-br'],
};

const enUS = {
    navigation: { ...navigationPt, ...navigationEn },
    home: { ...homePt, ...homeEn },
    solutions: { ...solutionsPt, ...solutionsEn },
    about: { ...aboutPt, ...aboutEn },
    footer: { ...footerPt, ...footerEn },
    faq: faqEn,
    careers: careersData['en'],
    legal: legalData['en'],
    cookieConsent: cookieConsentEn,
};

const esES = {
    navigation: { ...navigationPt, ...navigationEs },
    home: { ...homePt, ...homeEs },
    solutions: { ...solutionsPt, ...solutionsEs },
    about: { ...aboutPt, ...aboutEs },
    footer: { ...footerPt, ...footerEs },
    faq: faqEs,
    careers: careersData['es'],
    legal: legalData['es'],
    cookieConsent: cookieConsentEs,
};

const resources = {
    'pt-br': ptBR,
    'en': enUS,
    'es': esES
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationData = any;

interface PublicTranslationContextType {
    currentLanguage: string;
    setLanguage: (lang: string) => void;
    t: TranslationData;
}

const PublicTranslationContext = createContext<PublicTranslationContextType | undefined>(undefined);

export const PublicTranslationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentLanguage, setCurrentLanguage] = useState('pt-br');

    useEffect(() => {
        const savedLang = localStorage.getItem('nexu_language');
        if (savedLang && resources[savedLang as keyof typeof resources]) {
            setCurrentLanguage(savedLang);
        }
    }, []);

    const setLanguage = (lang: string) => {
        setCurrentLanguage(lang);
        localStorage.setItem('nexu_language', lang);
    };

    const t = resources[currentLanguage as keyof typeof resources] || resources['pt-br'];

    return (
        <PublicTranslationContext.Provider value={{ currentLanguage, setLanguage, t }}>
            {children}
        </PublicTranslationContext.Provider>
    );
};

export const usePublicTranslationContext = () => {
    const context = useContext(PublicTranslationContext);
    if (!context) {
        throw new Error("usePublicTranslationContext must be used within a PublicTranslationProvider");
    }
    return context;
};
